import React, { useEffect } from "react"
import { Member, MemberRole, MemberPermission } from "./types/memberTypes"
import MemberPermissionsCell from "./MemberPermissionsCell"
import MemberPermissionsTooltip from "./MemberPermissionsTooltip"
// import { getAuth } from "firebase/auth" // Removed
import { useFirebase } from "@/context/FirebaseProvider"; // Added
import { permissionOptions } from "./types/memberTypes"
// Avatar imports seem unused, can be removed if not needed by Member type or other logic
// import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar" 

interface MembersTableProps {
  members: Member[]
  editingMemberId: string | null
  isCurrentMemberPrimaryOwner: boolean
  getCheckedPermissionsCount: (permissions?: any[]) => number
  getCheckedPermissionNames: (permissions?: any[]) => string[]
  handleRemoveMemberClick?: (member: Member) => void
  handleEditClick?: (memberId: string) => void
  handleCancelEdit: () => void
  handleSaveEdit: () => void
  handleRoleChange: (memberId: string, role: MemberRole) => void
  handlePermissionChange: (
    memberId: string,
    permissionId: string,
    checked: boolean
  ) => void
  canManageBusinessMembers: boolean
  handleResendInvite?: (member: Member) => void
}

const MembersTable: React.FC<MembersTableProps> = ({
  members,
  editingMemberId,
  isCurrentMemberPrimaryOwner,
  getCheckedPermissionsCount,
  getCheckedPermissionNames,
  handleRemoveMemberClick,
  handleEditClick,
  handleCancelEdit,
  handleSaveEdit,
  handleRoleChange,
  handlePermissionChange,
  canManageBusinessMembers,
  handleResendInvite,
}) => {
  const { firebaseUser } = useFirebase(); // Added
  const currentAuthUserId = firebaseUser?.uid; // Use firebaseUser

  const ensureValidMembers = (membersList: Member[]): Member[] => {
    return membersList.map((member) => {
      // PRESERVE ORIGINAL ROLE AND PERMISSIONS - Don't override from defaults
      const originalRole = member.role
      const originalPermissions = member.permissions || []

      // Only create default permissions if none exist
      let updatedPermissions = originalPermissions
      if (!updatedPermissions || updatedPermissions.length === 0) {
        if (originalRole === "Owner" || originalRole === "Primary Owner") {
          updatedPermissions = permissionOptions
            .filter((option) => option.id !== "viewOnly")
            .map((defaultPerm) => {
              return {
                id: defaultPerm.id,
                name: defaultPerm.name,
                checked: true,
              }
            })
        } else {
          updatedPermissions = permissionOptions.map((p) => ({
            id: p.id,
            name: p.name,
            checked: p.id === "viewOnly",
          }))
        }
      }

      let enhancedMember = { ...member } // Start with the passed member data

      // Assuming parent component now provides fully enriched member data.
      // The logic to pull firstName, lastName, email, phone, avatarUrl from appData.users or appData.currentUser is removed.
      // The parent component is responsible for ensuring these fields are populated in the 'members' prop.

      // CRITICAL: Ensure original role and updatedPermissions (with defaults if necessary) are set.
      enhancedMember.role = originalRole;
      enhancedMember.permissions = updatedPermissions;
      // status should also be part of the member prop.

      const initials =
        enhancedMember.avatarInitials ||
        `${enhancedMember.firstName?.charAt(0) || ""}${
          enhancedMember.lastName?.charAt(0) || ""
        }`.trim() || (enhancedMember.email?.charAt(0) || '?').toUpperCase();


      const finalMember = {
        ...enhancedMember,
        // permissions: updatedPermissions, // Already set on enhancedMember
        avatarInitials: initials,
      }
      return finalMember
    })
  }

  const enhancedMembers = ensureValidMembers(members)

  return (
    <div className="border rounded-lg overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Member
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Role
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Permissions
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {enhancedMembers.map((member) => (
            <tr
              key={member.id}
              className={member.isCurrentUser ? "bg-blue-50" : ""}
            >
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="h-10 w-10 flex-shrink-0 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 uppercase mr-4">
                    {member.avatarInitials || member.firstName?.[0] || "?"}
                  </div>
                  <div className="flex flex-col">
                    <div className="text-sm font-medium text-gray-900">
                      {member.firstName} {member.lastName}
                      {member.isCurrentUser && " (You)"}
                    </div>
                    <div className="text-sm text-gray-500">{member.email}</div>
                    {/* {member.status === "invited" && (
                      <span className="inline-block mt-1 text-xs px-2 py-1 bg-amber-50 text-amber-600 rounded-full">
                        Pending Invitation
                      </span>
                    )} */}
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                  ${
                    member.role === "Primary Owner"
                      ? "bg-purple-100 text-purple-800"
                      : member.role === "Owner"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-green-100 text-green-800"
                  }`}
                >
                  {member.role}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {editingMemberId === member.id ? (
                  <MemberPermissionsCell
                    member={member}
                    handlePermissionChange={handlePermissionChange}
                  />
                ) : (
                  <MemberPermissionsTooltip
                    permissions={member.permissions}
                    getCheckedPermissionsCount={getCheckedPermissionsCount}
                    getCheckedPermissionNames={getCheckedPermissionNames}
                  >
                    <div className="cursor-help">
                      <div className="text-sm text-gray-900">
                        {getCheckedPermissionsCount(member.permissions)}{" "}
                        permissions
                      </div>
                    </div>
                  </MemberPermissionsTooltip>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex justify-end space-x-2">
                  {handleEditClick &&
                    member.id !== editingMemberId &&
                    !member.isCurrentUser && (
                      <button
                        className="text-indigo-600 hover:text-indigo-900"
                        onClick={() => handleEditClick(member.id)}
                      >
                        Edit
                      </button>
                    )}

                  {handleRemoveMemberClick &&
                    member.role !== "Primary Owner" &&
                    !member.isCurrentUser && (
                      <button
                        className="text-red-600 hover:text-red-900"
                        onClick={() => handleRemoveMemberClick(member)}
                      >
                        Remove
                      </button>
                    )}

                  {handleResendInvite &&
                    member.status === "invited" &&
                    !member.isCurrentUser && (
                      <button
                        className="text-blue-600 hover:text-blue-900"
                        onClick={() => handleResendInvite(member)}
                      >
                        Resend
                      </button>
                    )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default MembersTable
